import * as colorPicker from '@zag-js/color-picker';
import type { WebColor } from './ColorPicker';

export const parseWebColor = (color: WebColor) => {
  return colorPicker.parse(`rgba(${color.r}, ${color.g}, ${color.b}, ${color.a})`);
};

export const parseHexColor = (color: string) => {
  return colorPicker.parse(color);
};

export const parseColorPickerColor = (color: colorPicker.Color) => {
  const rgba = color.toFormat('rgba');
  return {
    r: rgba.getChannelValue('red'),
    g: rgba.getChannelValue('green'),
    b: rgba.getChannelValue('blue'),
    a: rgba.getChannelValue('alpha'),
  };
};

/**
 * Serialize a WebColor to `#RRGGBB` (uppercase, RGB only).
 * Keep in sync with `formatHex` in core's color-palette-node-executor.ts and the
 * `ColorPalette` "text" arm in design-core's graph/src/evaluation.rs.
 */
export function formatHexCode(color: WebColor): string {
  const hex = (n: number) => Math.round(n).toString(16).padStart(2, '0').toUpperCase();
  return `#${hex(color.r)}${hex(color.g)}${hex(color.b)}`;
}

const HEX_RUN_REGEX = /[^0-9a-f]*([0-9a-f]*)/i;

/**
 * Forgiving hex-code normalizer. Skips leading non-hex chars, then:
 *   - ≥6 hex chars → takes the first 6 (`dafdfadfa` → `#DAFDFA`)
 *   - 3-5 hex chars → CSS-shorthand expansion of the first 3 (`abc` → `#AABBCC`)
 *   - 2 hex chars → greyscale repeat (`ab` → `#ABABAB`)
 *   - 1 hex char → greyscale repeat (`a` → `#AAAAAA`)
 *   - 0 hex chars → null
 *
 * RGB only; alpha is handled by the separate AlphaEditor.
 */
export function parseHexCode(input: string): string | null {
  const match = HEX_RUN_REGEX.exec(input.trim());
  const group = match?.[1];
  if (!group) return null;

  let hex: string;
  if (group.length >= 6) {
    hex = group.substring(0, 6);
  } else if (group.length >= 3) {
    hex = group
      .substring(0, 3)
      .split('')
      .map((c) => c + c)
      .join('');
  } else if (group.length === 2) {
    hex = group + group + group;
  } else {
    hex = group.repeat(6);
  }

  return `#${hex.toUpperCase()}`;
}
